import { LIVE_API, ENDPOINTS, MAIL } from './config'
import { apiPost, ApiRequestError } from './apiClient'
import { notifyTeam } from './notify'
import type { ApplicationInput } from './careersSchema'

export type UploadField = 'cv' | 'cover_letter'

interface PresignResponse {
  uploadUrl: string
  key: string
}

export interface UploadResult {
  key: string
  filename: string
}

/** docx files sometimes arrive with an empty type, so derive it from the name. */
export function contentTypeFor(file: File): string {
  if (file.type) return file.type
  return file.name.toLowerCase().endsWith('.pdf')
    ? 'application/pdf'
    : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
}

function putWithProgress(url: string, file: File, contentType: string, onProgress: (pct: number) => void) {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('Content-Type', contentType)
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress(Math.round((event.loaded / event.total) * 100))
    }
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject(new ApiRequestError('The file upload did not complete. Please try again.', xhr.status))
    xhr.onerror = () =>
      reject(new ApiRequestError('We could not upload your file. Please check your connection.', 0))
    xhr.send(file)
  })
}

/** Preview only: a believable progress run, so the interface can be tested end to end. */
function simulateUpload(onProgress: (pct: number) => void) {
  return new Promise<void>((resolve) => {
    let pct = 0
    const timer = window.setInterval(() => {
      pct = Math.min(pct + 20, 100)
      onProgress(pct)
      if (pct >= 100) {
        window.clearInterval(timer)
        resolve()
      }
    }, 120)
  })
}

/**
 * Presign, then PUT straight to object storage. The file never passes through
 * our API and is never stored in the database, only its key.
 */
export async function uploadApplicationFile(
  file: File,
  field: UploadField,
  onProgress: (pct: number) => void,
): Promise<UploadResult> {
  const contentType = contentTypeFor(file)

  if (!LIVE_API) {
    await simulateUpload(onProgress)
    const now = new Date()
    const id = Math.random().toString(36).slice(2, 10)
    return {
      key: `applications/${now.getFullYear()}/${String(now.getMonth() + 1).padStart(2, '0')}/${id}/${field}${
        file.name.toLowerCase().endsWith('.pdf') ? '.pdf' : '.docx'
      }`,
      filename: file.name,
    }
  }

  const presign = await apiPost<PresignResponse>(ENDPOINTS.careersPresign, {
    filename: file.name,
    contentType,
    field,
  })
  await putWithProgress(presign.uploadUrl, file, contentType, onProgress)
  return { key: presign.key, filename: file.name }
}

export interface SubmitResult {
  ok: boolean
  error?: string
  /** False on the preview, where no email can be sent. */
  emailSent: boolean
}

export async function submitApplication(application: ApplicationInput): Promise<SubmitResult> {
  if (!LIVE_API) {
    // No server yet, so files are not stored and the applicant gets no email.
    // The team is still told through the relay, with every answer.
    const fields: Record<string, string> = {}
    for (const [key, value] of Object.entries(application as unknown as Record<string, unknown>)) {
      if (value === undefined || value === null || value === '' || /s3key/i.test(key)) continue
      const label = key.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase())
      fields[label] = typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)
    }
    const name = [fields['First Name'], fields['Last Name']].filter(Boolean).join(' ')
    await notifyTeam(`New application: ${fields['Position Slug'] ?? 'internship'} from ${name || 'an applicant'}`, {
      ...fields,
      Note: 'Preview site: the CV and cover letter files are not stored yet, only their names are listed.',
      'Submitted at': new Date().toUTCString(),
    })
    return { ok: true, emailSent: false }
  }

  try {
    await apiPost<{ id: string }>(ENDPOINTS.careersApply, { ...application, notifyTo: MAIL.hiring })
    return { ok: true, emailSent: true }
  } catch (error) {
    return {
      ok: false,
      emailSent: false,
      error:
        error instanceof ApiRequestError
          ? error.message
          : 'We could not submit your application. Your answers are still here, so please try again.',
    }
  }
}
